/* eslint-disable no-bitwise */
import { ensureExhaustive } from "@dhau/lang-extras";
import { uint8ArrayAlloc } from "../utils/uint8-array.ts";
import type { Bitmap } from "./types.ts";
import { bitmapChannels } from "./types.ts";

function rotateBitmapAtRightAngles(bitmap: Bitmap, deg: 0 | 90 | 180 | 270) {
	if (Math.abs(deg) === 0) {
		return bitmap;
	}

	const { width, height } = bitmap;

	let angle;
	switch (deg) {
		case 0:
			angle = 0;
			break;
		case 90:
			angle = 90;
			break;
		case 180:
			angle = 180;
			break;
		case 270:
			angle = -90;
			break;
		default:
			ensureExhaustive(deg);
	}
	// After this switch block, angle will be 90, 180 or -90

	const newWidth = angle === 180 ? width : height;
	const newHeight = angle === 180 ? height : width;

	// Allocate destination byte buffer and prepare 32-bit views for fast pixel copies
	const dstBytes = uint8ArrayAlloc(newWidth * newHeight * bitmapChannels);

	// Create typed 32-bit views over source and destination
	const srcBytes = bitmap.data as unknown as Uint8Array;
	const src32 = new Uint32Array(
		srcBytes.buffer,
		srcBytes.byteOffset,
		width * height,
	);
	const dst32 = new Uint32Array(dstBytes.buffer, 0, newWidth * newHeight);

	switch (angle) {
		case 90: {
			for (let y = 0; y < height; y++) {
				const yTimesW = y * width;
				for (let x = 0; x < width; x++) {
					const srcIndex = yTimesW + x;
					const dstIndex = (width - 1 - x) * newWidth + y;
					dst32[dstIndex] = src32[srcIndex];
				}
			}
			break;
		}
		case -90: {
			for (let y = 0; y < height; y++) {
				const yTimesW = y * width;
				for (let x = 0; x < width; x++) {
					const srcIndex = yTimesW + x;
					const dstIndex = x * newWidth + (height - 1 - y);
					dst32[dstIndex] = src32[srcIndex];
				}
			}
			break;
		}
		case 180: {
			for (let y = 0; y < height; y++) {
				const yTimesW = y * width;
				const dstRowBase = (height - 1 - y) * newWidth;
				for (let x = 0; x < width; x++) {
					const srcIndex = yTimesW + x;
					const dstIndex = dstRowBase + (width - 1 - x);
					dst32[dstIndex] = src32[srcIndex];
				}
			}
			break;
		}
		default:
			throw new Error("Unsupported matrix rotation angle");
	}

	return {
		data: dstBytes,
		width: newWidth,
		height: newHeight,
	};
}

export { rotateBitmapAtRightAngles };
